/**
 * Helpers for splitting "Infrastructure Fees" (programme-wide Guide and S&T
 * universal fees) out of the daily itinerary. Portage and Staff Hotel still
 * remain linked to specific days and are NOT considered infrastructure fees.
 */

interface InfrastructureCandidate {
  item_type?: string | null;
  universal_fee_kind?: string | null;
  staff_categories?: string[] | null;
}

export function isInfrastructureFee(item: InfrastructureCandidate): boolean {
  return (
    item.item_type === 'universal_fee' &&
    (item.universal_fee_kind === 'guiding' || item.universal_fee_kind === 'assistant_guide' ||
      item.universal_fee_kind === 'st' ||
      item.universal_fee_kind === 'chef' ||
      item.universal_fee_kind === 'fejo')
  );
}

/**
 * Splits items into the infrastructure fees block (Guide first, then S&Ts)
 * and the remaining day-linked items.
 */
export function splitInfrastructureFees<T extends InfrastructureCandidate>(
  items: T[]
): { infrastructure: T[]; dayItems: T[] } {
  const infrastructure: T[] = [];
  const dayItems: T[] = [];

  for (const item of items) {
    if (isInfrastructureFee(item)) {
      infrastructure.push(item);
    } else {
      dayItems.push(item);
    }
  }

  infrastructure.sort((a, b) => {
    const rank = (k?: string | null) =>
      k === 'guiding' ? 0 : k === 'assistant_guide' ? 1 : k === 'st' ? 2 : k === 'chef' ? 3 : k === 'fejo' ? 4 : 5;
    return rank(a.universal_fee_kind) - rank(b.universal_fee_kind);
  });

  return { infrastructure, dayItems };
}

/**
 * Returns the human-readable staff category label for an S&T row
 * (e.g. "Driver", "Guide", "Other"). Empty string if absent.
 */
export function formatStaffCategory(item: InfrastructureCandidate): string {
  const cat = item.staff_categories?.[0];
  if (!cat) return '';
  if (cat === 'assistant_guide') return 'Assistant Guide';
  return cat.charAt(0).toUpperCase() + cat.slice(1);
}

/**
 * Returns the label used for an infrastructure fee row in the
 * Programme Breakdown / financial table.
 */
export function infrastructureFeeLabel(
  item: InfrastructureCandidate,
  baseTitle: string
): string {
  if (item.universal_fee_kind === 'guiding') {
    return `Infrastructure fees: Guide`;
  }
  if (item.universal_fee_kind === 'assistant_guide') {
    return `Infrastructure fees: Assistant Guide`;
  }
  if (item.universal_fee_kind === 'st') {
    const cat = formatStaffCategory(item);
    return cat
      ? `Infrastructure fees: S&Ts — ${cat}`
      : `Infrastructure fees: S&Ts`;
  }
  if (item.universal_fee_kind === 'chef') {
    return `Infrastructure fees: Private Chef`;
  }
  if (item.universal_fee_kind === 'fejo') {
    return `Infrastructure fees: FEJO Consulting Fee`;
  }
  return baseTitle;
}